/**
 * THE LOADER (C59, R47): THE ONLY FILE THAT NAMES GOOGLE'S TAG HOST, and the only code that puts anything in the data
 * layer. `outsideHosts.test.ts` fails if any other shipped file names the host or the data layer.
 *
 * WHEN THE CONTAINER IS PUT ON THE PAGE: only when `consent.ts` decides `granted` (a yes to the current text, read
 * back from the cookie jar, reconciled with the account), on an allowed host, with a container id built in, and on a
 * page whose address carries no credential.
 *
 * THE ORDER (Consent Mode v2, analytics spec *Loading order*; Google: "call the `gtag('consent', 'default', ...)`
 * command on every page of your site before any commands that send measurement data"):
 *   1. `dataLayer` and the `gtag()` shim, which pushes its `arguments` (an array would be a plain entry, no command);
 *   2. `gtag('consent', 'default', ...)` with all seven types denied;
 *   3. `gtag('consent', 'update', { analytics_storage: 'granted' })`; the advertising types stay denied for good;
 *   4. the context entry: the scrubbed page fields and the user ref (or null);
 *   5. `gtm.start`, then the container's script, from this code and never from `index.html`.
 * This is "basic" consent mode: nothing reaches Google before step 5. Then the entries queued for this page life, in
 * order, then the current page's view.
 *
 * CREDENTIAL ADDRESSES never inject it, whatever the answer: `/invite/:code` (a single-use invite), `/signed-in`, and
 * any address with a fragment. Their page view (scrubbed) and their events wait in memory and go out if the container
 * loads later in the same page life, on the next route; a full navigation loses them. A creator's link, `/i/:code`,
 * is the one exception (Q7): the visitor goes to the sign-in provider next, so a queued view would never be sent;
 * there the container loads as on any page, and the code reaches no field (`scrub.ts`, `campaigns.ts`). A container
 * already on the page stays there when the person later opens a credential address in the app: a container cannot
 * leave a page, it reads nothing but the data layer (#710's container test), and that page's fields are templates.
 *
 * WITHDRAWAL: the cookie becomes `d`, Consent Mode is told `analytics_storage: 'denied'`, every `_ga` and `_ga_*`
 * cookie is deleted on the registrable domain, and the page reloads, so the container is no longer on it. A
 * withdrawal holds for the rest of the page life whatever the jar says.
 *
 * SIGN-OUT keeps the browser's answer (it is the browser's) and deletes Google's cookies, so the next person on a
 * shared computer is a new client and is not stitched to this one's User-ID; with the container on the page it pushes
 * `gw_user_ref: null` at once (Google: "When a user signs out, set `user_id` to `null`"), stops this page life's
 * measuring, and asks the caller for a full page load instead of a navigation.
 *
 * A STALE TAB: on focus, on being shown and on a page restored from the back-forward cache the jar is read again. A
 * cookie another tab changed makes the account's last read stale too: nothing is decided until `GET /api/me` has
 * answered again, so a withdrawal in another tab is taken, never read as a conflict.
 */
import { campaignQuery, checkCampaign, type Campaign } from './campaigns'
import { ANALYTICS_TEXT_VERSION, CONTAINER_ID_PATTERN, offReason } from './config'
import {
  accountAnswer,
  deleteGoogleCookies,
  readAnswer,
  reconcile,
  serializeAnswer,
  writeAnswer,
  type Account,
  type Answer,
  type Choice,
  type ConsentState,
  type CookieJar,
  type Decision,
  type Mode,
} from './consent'
import { buildContext, buildEntry, checkEvent, setSink, type CheckedEvent, type DataLayerEntry } from './events'
import { pageFields, routeFor, scrubReferrer, type PageFields } from './scrub'

/** The address of the container's script. The id is added as a query parameter. */
const CONTAINER_SCRIPT = 'https://www.googletagmanager.com/gtm.js'

export const CONSENT_TYPES = [
  'ad_storage',
  'ad_user_data',
  'ad_personalization',
  'analytics_storage',
  'functionality_storage',
  'personalization_storage',
  'security_storage',
] as const

/** GA's User-ID is the opaque ref and nothing else: 16 lowercase hex characters (`api/platform/observability.py#user_ref_for`). */
export const USER_REF_PATTERN = /^[0-9a-f]{16}$/

/** At most this many entries wait for the container in one page life; later ones are dropped. */
const QUEUE_LIMIT = 50

export interface LoaderWindow {
  dataLayer?: unknown[]
}

export interface LoaderDocument extends CookieJar {
  createElement(tag: 'script'): { async: boolean; src: string }
  head: { appendChild(node: unknown): unknown }
}

/** What the page knows of the account, read from `GET /api/me`: the analytics consent and the ref, never the user id. */
export interface AccountRead {
  account: Account
  /** `me.analytics_user_ref` as the server sent it; checked here before it is used. */
  userRef: string | null | undefined
  /** When that `me` answered, in milliseconds (0 without one). */
  updatedAt: number
}

/** A page's address, as the router reports it. */
export interface PageLocation {
  pathname: string
  search: string
  hash: string
}

export interface AnalyticsEnv {
  containerId: string
  hostname: string
  /** `document.referrer` at boot: the first page's referrer. */
  referrer: string
  win: LoaderWindow
  doc: LoaderDocument
  /** Milliseconds since the epoch. */
  now(): number
  account(): AccountRead
  /** Sends the `web_visitor` record of a browser's answer the account had not been given (rows 4 and 5). */
  record(answer: Answer): void
  /** Asks `GET /api/me` again (for the ref after a grant, and after another tab changed the cookie). */
  refreshMe(): void
  /** Reloads the page with `WITHDRAWN_MARKER` in its address. */
  reloadWithdrawn(): void
  /** The address carried the marker at boot (the page a withdrawal reloaded). */
  justWithdrawn: boolean
}

/** What a screen draws from (#709): the decision, the two answers it was made from, and the page's facts. */
export interface AnalyticsState {
  mode: Mode
  /**
   * Why it is off: `no-container-id` (the build has none), `not-production-host`, `no-analytics-consent` (a `me` with
   * no analytics member), `other-text-in-force` (this build's text is not the server's), `unknown-status`, `not-started`.
   */
  reason?: string
  /** On `ask`: either side holds an answer to an older text. That is an answer: nothing asks unprompted (CO-D4). */
  changed?: boolean
  /** On `conflict`: both answers and their dates (AA-D5). */
  conflict?: Decision['conflict']
  /** The answer in this browser's cookie, whatever its version. */
  browser: Answer | null
  /** The account's analytics consent as `GET /api/me` reported it, or null without a session or before it answered. */
  account: ConsentState | null
  /** A session is known: the account's answer counts. */
  signedIn: boolean
  /** This page is the one a withdrawal reloaded: `withdrawnNow` is said once (AA-D7). */
  withdrawnNow: boolean
}

/** The `gtag()` function over a data layer: Google's code reads `arguments` objects from it, never arrays. */
function shim(layer: unknown[]): (...args: unknown[]) => void {
  return function gtag() {
    // eslint-disable-next-line prefer-rest-params
    layer.push(arguments)
  }
}

export const NOT_STARTED: AnalyticsState = { mode: 'off', reason: 'not-started', browser: null, account: null, signedIn: false, withdrawnNow: false }

/** The routes whose page holds a credential in its address or has just received one. */
const CREDENTIAL_ROUTES: readonly string[] = ['/invite/:code', '/signed-in']

/**
 * True for an address whose page must never inject the container: a fragment, or a path the router renders as one of
 * the credential routes. The route is the scrub's row, matched as the router matches (`/invite/CODE//` and
 * `/%69nvite/CODE` are the invite page too).
 */
export function isCredentialAddress(location: PageLocation): boolean {
  return location.hash !== '' || CREDENTIAL_ROUTES.includes(routeFor(location.pathname).template)
}

/** The controller's face: what `index.ts` and the tests call. */
export interface AnalyticsController {
  /** Boot: the first page of the page life. */
  start(location: PageLocation): void
  /** A route change. The same path again (a state-only or query-only change) is not a new page. */
  route(location: PageLocation): void
  /** Read the jar and the account again, and act on what they say now. */
  refresh(): void
  /**
   * The person answered (#709): on the band (a visitor), or once the server stored the answer (signed in). Writes the
   * cookie and reads it back. A yes loads the container (on a page that may carry it); its page view goes first, so a
   * carried campaign rides on the first hit, then `consent_changed` for the grant. A no on a page that loaded the
   * container is a withdrawal. Returns whether the browser kept the answer (AA-D9's state when it did not).
   */
  answer(choice: Choice): { kept: boolean }
  /**
   * Withdrawal (#709): after the server recorded it, when signed in. The cookie becomes `d`, Google is told no, its
   * cookies go, and the page reloads with the marker. A visitor's browser that would not keep the `d` is not reloaded
   * (the old yes would come back with the page): this page stops and stays. Returns whether the cookie was kept.
   */
  withdraw(): boolean
  /**
   * Sign-out (#708-2): after `signOut()` resolved, before leaving the page. Returns whether the caller must end with a
   * full page load (`window.location.assign('/login')`) instead of a navigation: true when the container is on the page.
   */
  signOut(): { fullPageLoad: boolean }
  getState(): AnalyticsState
  subscribe(listener: () => void): () => void
}

interface Page {
  location: PageLocation
  fields: PageFields
  /** Its page view has been pushed or queued. */
  viewed: boolean
}

/**
 * The decision applied to one page life. A closure rather than a class, so the bundle's minifier can shorten every
 * name in it (the module's share of the bundle is a measure, analytics spec *Quality*).
 */
export function createAnalytics(env: AnalyticsEnv): AnalyticsController {
  const off = offReason(env.hostname, env.containerId)
  const listeners = new Set<() => void>()
  /** The `web_visitor` records sent in this page life, by answer and version (ruling R3: never on every read of me). */
  const recorded = new Set<string>()
  let state = NOT_STARTED
  let decision: Decision = { mode: 'off', reason: off ?? 'not-started' }
  let page: Page | null = null
  let loaded = false
  let stopped = false
  /** A withdrawal on this page holds for its page life. */
  let withdrawn = false
  /** The page is ending (a reload after a withdrawal, a sign-out with the container on it): nothing more is measured. */
  let ended = false
  let queue: DataLayerEntry[] = []
  let campaign: Campaign | null = null
  let userRef: string | null = null
  let pushedRef: string | null = null
  /** The answer both sides last held together, while this page was open. */
  let agreed: Choice | null = null
  /** The answer in the cookie as this page last read or wrote it (undefined before the first read). */
  let jar: string | null | undefined
  /** A cookie changed under this page at this time: an account read from before it is stale. */
  let staleBefore = 0
  let signedOutAt = 0

  const seconds = () => Math.floor(env.now() / 1000)
  const layer = () => (env.win.dataLayer ??= [])
  const gtag = (...args: unknown[]) => shim(layer())(...args)
  const ownAnswer = () => {
    const answer = readAnswer(env.doc.cookie)
    return answer && serializeAnswer(answer)
  }

  function readAccount(): AccountRead {
    const read = env.account()
    if (read.account.kind !== 'ready') return read
    if (read.updatedAt <= signedOutAt) return { account: { kind: 'visitor' }, userRef: null, updatedAt: 0 }
    if (read.updatedAt < staleBefore) return { account: { kind: 'loading' }, userRef: null, updatedAt: read.updatedAt }
    return read
  }

  /** The account's answer to the text in force, when it is a `d`. */
  function accountSaysNo(): boolean {
    const read = readAccount()
    const held = read.account.kind === 'ready' && read.account.state ? accountAnswer(read.account.state) : null
    return held !== null && held !== 'unknown' && held.choice === 'd'
  }

  /** Writes an answer into the cookie; true when the jar kept it. This page's own write is not "another tab's". */
  function write(answer: Answer): boolean {
    const kept = writeAnswer(env.doc, answer)
    jar = ownAnswer()
    return kept
  }

  /** The one place an entry enters the data layer. The first page view after a grant carries the checked campaign. */
  function push(entry: DataLayerEntry): void {
    if (entry.event === 'page_view' && campaign) {
      entry = { ...entry, gw_page_location: entry.gw_page_location + campaignQuery(campaign) }
      campaign = null
    }
    layer().push(entry)
  }

  /** Takes analytics storage back on a page that loaded the container. A container cannot leave a page. */
  function stop(): void {
    if (!loaded || stopped) return
    gtag('consent', 'update', { analytics_storage: 'denied' })
    stopped = true
  }

  /** An event, now or later or never: what the decision says at the moment it is made. */
  function emit(checked: CheckedEvent): void {
    if (page === null || ended) return
    // A hidden tab hears of another tab's withdrawal only when it is shown again; an event of its own (a save that
    // resolves) reads the jar first, so it is never sent on a yes the cookie no longer holds.
    if (jar !== undefined && ownAnswer() !== jar) refresh()
    if (ended) return
    const entry = buildEntry(checked, page.fields)
    if (decision.mode === 'granted' && loaded && !stopped) push(entry)
    else if ((decision.mode === 'granted' || decision.mode === 'pending') && queue.length < QUEUE_LIMIT) queue.push(entry)
    else return // without a yes nothing is kept, and past the limit nothing more
    if (checked.event === 'page_view') page.viewed = true
  }

  const view = () => emit({ event: 'page_view', params: {} })

  /** Steps 1 to 5 of the order above. */
  function inject(fields: PageFields): void {
    if (!CONTAINER_ID_PATTERN.test(env.containerId)) return
    gtag('consent', 'default', Object.fromEntries(CONSENT_TYPES.map((type) => [type, 'denied'])))
    gtag('consent', 'update', { analytics_storage: 'granted' })
    layer().push(buildContext(fields, userRef))
    pushedRef = userRef
    layer().push({ 'gtm.start': env.now(), event: 'gtm.js' })
    const script = env.doc.createElement('script')
    script.async = true
    const address = new URL(CONTAINER_SCRIPT)
    address.searchParams.set('id', env.containerId)
    script.src = address.href
    env.doc.head.appendChild(script)
    loaded = true
  }

  /** The decision is `granted`: load, or take up again, and send what waited. */
  function go(current: Page): void {
    if (loaded) {
      if (stopped) {
        gtag('consent', 'update', { analytics_storage: 'granted' })
        stopped = false
      }
      if (userRef !== pushedRef) {
        push(buildContext(current.fields, userRef))
        pushedRef = userRef
      }
    } else {
      if (isCredentialAddress(current.location)) return
      inject(current.fields)
    }
    const waiting = queue
    queue = []
    for (const entry of waiting) push(entry)
    if (!current.viewed) view()
  }

  function publish(): void {
    const account = off === null && page !== null ? readAccount().account : undefined
    const next: AnalyticsState = {
      mode: decision.mode,
      reason: decision.reason,
      changed: decision.changed,
      conflict: decision.conflict,
      browser: readAnswer(env.doc.cookie),
      account: account?.kind === 'ready' ? (account.state ?? null) : null,
      signedIn: account !== undefined && account.kind !== 'visitor',
      withdrawnNow: env.justWithdrawn,
    }
    if (JSON.stringify(next) === JSON.stringify(state)) return
    state = next
    for (const listener of listeners) listener()
  }

  function refresh(): void {
    if (off !== null || page === null || ended) return
    const now = env.now()
    const answer = readAnswer(env.doc.cookie)
    const seen = answer && serializeAnswer(answer)
    if (jar !== undefined && seen !== jar) {
      // Another tab (or the landing site) changed the answer: what this page last read of the account predates it.
      staleBefore = now
      env.refreshMe()
    }
    jar = seen

    const read = readAccount()
    let browser = answer
    // An account that moved from the yes both sides held while this page was open to a no is the person's newest
    // answer (a withdrawal in Settings lands here before the cookie is written): the browser takes the no. ONLY IN THAT
    // DIRECTION, the safe one. An account that moves to a yes against a no in this browser (a grant on another device)
    // is the table's conflict: nothing loads and nothing asks (CO-D4), and so for any disagreement found at a first read.
    if (read.account.kind === 'ready' && read.account.state && agreed === 'g' && answer?.choice === 'g') {
      const held = accountAnswer(read.account.state)
      if (held !== null && held !== 'unknown' && held.choice === 'd') browser = null
    }

    decision = reconcile(browser, read.account, ANALYTICS_TEXT_VERSION, Math.floor(now / 1000))
    if (withdrawn && decision.mode === 'granted') decision = { mode: 'declined' }
    const { mode } = decision

    if (decision.browserTakes) write(decision.browserTakes)
    if (decision.accountRecords) {
      const key = `${decision.accountRecords.choice}.${decision.accountRecords.version}`
      if (!recorded.has(key)) {
        recorded.add(key)
        env.record(decision.accountRecords)
      }
    }
    if (mode === 'granted' || mode === 'declined') agreed = read.account.kind === 'ready' ? (mode === 'granted' ? 'g' : 'd') : null
    else if (mode !== 'pending') agreed = null

    const ref = read.userRef
    userRef = mode === 'granted' && typeof ref === 'string' && USER_REF_PATTERN.test(ref) ? ref : null

    if (mode === 'granted') go(page)
    else {
      stop()
      if (mode !== 'pending') queue = []
      // ask, declined, conflict: nothing of Google's stays without a current yes.
      if (mode !== 'pending' && mode !== 'off') deleteGoogleCookies(env.doc)
    }
    publish()
  }

  function enter(location: PageLocation, referrer: string): void {
    const tag = checkCampaign(location.search, location.pathname)
    if (tag) campaign = tag
    const next: Page = { location, fields: pageFields(env.hostname, location.pathname, referrer), viewed: false }
    page = next
    refresh()
    if (!next.viewed) view()
  }

  function withdraw(): boolean {
    if (off !== null) return false
    const kept = write({ choice: 'd', version: ANALYTICS_TEXT_VERSION, at: seconds() })
    withdrawn = true
    stop()
    queue = []
    deleteGoogleCookies(env.doc)
    refresh()
    if (kept || accountSaysNo()) {
      ended = true
      env.reloadWithdrawn()
    }
    return kept
  }

  return {
    start(location) {
      if (off !== null) {
        publish()
        return
      }
      setSink(emit)
      enter(location, scrubReferrer(env.referrer))
    },
    route(location) {
      if (off !== null || page === null) return
      if (location.pathname !== page.location.pathname) return enter(location, page.fields.gw_page_location)
      page.location = location
      refresh()
    },
    refresh,
    answer(choice) {
      if (off !== null) return { kept: false }
      if (choice === 'd' && loaded && !stopped) return { kept: withdraw() }
      const kept = write({ choice, version: ANALYTICS_TEXT_VERSION, at: seconds() })
      if (choice === 'g') withdrawn = false
      refresh()
      if (choice === 'g' && decision.mode === 'granted') {
        const granted = checkEvent('consent_changed', { consent: 'analytics', state: 'granted' })
        if (granted) emit(granted)
        if (readAccount().account.kind !== 'visitor') env.refreshMe()
      }
      return { kept }
    },
    withdraw,
    signOut() {
      if (off !== null) return { fullPageLoad: false }
      const fullPageLoad = loaded
      signedOutAt = env.now()
      if (loaded && page) {
        push(buildContext(page.fields, null))
        pushedRef = null
        stop()
        ended = true
      }
      userRef = null
      agreed = null
      queue = []
      deleteGoogleCookies(env.doc)
      // Nothing is decided anew here: the next page (a navigation, or the full load) reads the browser's answer alone.
      publish()
      return { fullPageLoad }
    },
    getState: () => state,
    subscribe(listener) {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
  }
}
